import { Badge, Box, CloseButton, Heading, HStack, Icon, Stack, Text, useColorModeValue } from '@ecoinvent/ui';
import { BiCalendar, BiRuler } from 'react-icons/bi';
import { FiBox, FiPieChart } from 'react-icons/fi';
import { IoEarth } from 'react-icons/io5';

import { activityTypes } from 'pages/Search/components/ProductSearchResultCard/ProductSearchResultTable/constants';
import { formatNumericalValue } from 'utilities/numbers';

import { CompareDataset } from './types';

type CardProps = {
  dataset: CompareDataset;
  onRemove?: () => void;
};

const DetailLine = ({ icon, children }: { icon: React.ElementType; children: React.ReactNode }) => {
  const grayText = useColorModeValue('gray.700', 'gray.400');
  return (
    <HStack spacing={2} color={grayText} fontSize="sm">
      <Icon as={icon} flexShrink={0} />
      <Text>{children}</Text>
    </HStack>
  );
};

export const CompareDatasetCard = ({ dataset, onRemove }: CardProps) => {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const grayText = useColorModeValue('gray.600', 'gray.400');
  const bg = useColorModeValue('white', 'whiteAlpha.100');

  return (
    <Box position="relative" flex={1} minW={0} borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={bg} p={4}>
      {onRemove && (
        <CloseButton
          position="absolute"
          top={2}
          right={2}
          size="sm"
          onClick={onRemove}
          aria-label="Remove dataset from comparison"
        />
      )}
      <HStack align="flex-start" justify="space-between" spacing={6} pr={6}>
        <Stack spacing={2} minW={0}>
          <Text fontSize="xs" color={grayText}>
            {dataset.version}-{dataset.systemModel}
          </Text>
          <Heading size="sm">{dataset.activityName}</Heading>
          <Text fontSize="sm" color={grayText}>
            {dataset.referenceProduct}
          </Text>
          <HStack spacing={2} pt={1} flexWrap="wrap">
            {dataset.activityType && (
              <Badge variant="outline" colorScheme="green" display="flex" alignItems="center" gap={1} textTransform="uppercase">
                <Icon as={FiBox} />
                {activityTypes[dataset.activityType]}
              </Badge>
            )}
            <Badge variant="solid" colorScheme="green">
              Activity
            </Badge>
          </HStack>
        </Stack>
        <Stack spacing={2} minW={0}>
          {typeof dataset.impactScore === 'number' && (
            <HStack spacing={2} fontSize="sm">
              <Text color={grayText} textTransform="uppercase">
                Impact:
              </Text>
              <Text fontWeight="bold">
                {formatNumericalValue(dataset.impactScore)} {dataset.impactUnit}
              </Text>
            </HStack>
          )}
          <DetailLine icon={IoEarth}>{dataset.geography}</DetailLine>
          {dataset.sector && <DetailLine icon={FiPieChart}>{dataset.sector}</DetailLine>}
          {dataset.timePeriod && <DetailLine icon={BiCalendar}>{dataset.timePeriod}</DetailLine>}
          <DetailLine icon={BiRuler}>{dataset.unit}</DetailLine>
        </Stack>
      </HStack>
    </Box>
  );
};

export const CompareDatasetPlaceholder = ({ index }: { index: number }) => {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const grayText = useColorModeValue('gray.500', 'gray.500');

  return (
    <Stack
      flex={1}
      minW={0}
      borderWidth="1px"
      borderColor={borderColor}
      borderStyle="dashed"
      borderRadius="lg"
      p={4}
      align="center"
      justify="center"
      spacing={1}
    >
      <Text fontWeight="semibold" color={grayText}>
        Dataset {index}
      </Text>
      <Text fontSize="sm" color={grayText}>
        add dataset to compare
      </Text>
    </Stack>
  );
};
